import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { fetchMe, loginRequest, logoutRequest, type AppUser } from '@/lib/api';

const ME_KEY = ['auth', 'me'] as const;

type AuthContextValue = {
  user: AppUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  /** Rzuca `ApiError` z komunikatem z backendu przy błędnych danych. */
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (module: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data: user, isPending } = useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });

  const loginMut = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => loginRequest(email, password),
    onSuccess: (u) => {
      qc.setQueryData(ME_KEY, u);
      /**
       * ⚠️⚠️ UNIEWAŻNIAMY WSZYSTKO PO ZALOGOWANIU — poprawka błędu zgłoszonego u klienta
       * (02.09.2026: „ma wszystko puste, dopóki nie kliknie ctrl+shift+R"). Zapytania, które
       * poleciały PRZED sesją, mają w pamięci `401`, a `staleTime` trzyma ten błąd nawet pięć
       * minut. Bez unieważnienia jedyną drogą wyjścia było przeładowanie strony — czyli coś,
       * czego człowiek po udanym logowaniu nie ma powodu robić.
       *
       * ⚠️ `invalidateQueries` BEZ KLUCZA, a nie lista wyjątków: to samo dotyczy każdego
       * zapytania, które kiedyś stanie się globalne. Wyliczanie ich po nazwie znaczyłoby, że
       * następne trzeba pamiętać dopisać — a zapomniane objawiłoby się jako „puste ekrany
       * po pierwszym logowaniu", czyli błąd, którego nikt nie skojarzy z tym miejscem.
       *
       * ⚠️ `ME_KEY` USTAWIAMY PRZED unieważnieniem, żeby ponowne zapytania poleciały już
       * z rozstrzygniętą sesją.
       */
      void qc.invalidateQueries();
    },
  });

  const logoutMut = useMutation({
    mutationFn: logoutRequest,
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMut.mutateAsync({ email, password });
    },
    [loginMut],
  );

  const logout = useCallback(async () => {
    await logoutMut.mutateAsync();
  }, [logoutMut]);

  const current = user ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user: current,
      isLoading: isPending,
      isAdmin: current?.role === 'ADMIN',
      login,
      logout,
      /**
       * Wyłącznie lista EFEKTYWNA z serwera — bez skrótu na roli.
       *
       * Wcześniej stało tu `role === 'ADMIN' || modules.includes(...)`, czyli dokładna kopia
       * skrótu, który był dziurą w `ModulesGuard`. Admin KLIENTA jest adminem, więc front
       * rysował mu całe menu, a API odmawiało — a od 2026-08-03 odmawia naprawdę.
       * Decyzję podejmuje jedna strona i tylko jedna.
       */
      can: (module: string) => current?.effectiveModules.includes(module) ?? false,
    }),
    [current, isPending, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth musi być wewnątrz AuthProvider');
  return ctx;
}
